import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, GitBranch, Star, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { shortRepoUrl } from "../components/issues/repo-select";
import { EmptyState, PageSkeleton } from "../components/page";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useIsAdmin } from "../lib/permissions";

export function RepoDetailPage() {
  const { key = "" } = useParams();
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["repo", key], queryFn: () => api.repo(key) });
  const repo = query.data?.repo;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["repo", key] });
    void queryClient.invalidateQueries({ queryKey: ["repos"] });
    void queryClient.invalidateQueries({ queryKey: ["issues-meta"] });
  };
  const onError = (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : "Update failed");
  const update = useMutation({
    mutationFn: (patch: Partial<{ name: string; url: string; defaultBranch: string }>) =>
      api.updateRepo(repo!.key, patch),
    onSuccess: refresh,
    onError,
  });
  const setActive = useMutation({
    mutationFn: (active: boolean) => api.setRepoActive(repo!.key, active),
    onSuccess: refresh,
    onError,
  });
  const setDefault = useMutation({
    mutationFn: () => api.setRepoDefault(repo!.key),
    onSuccess: () => {
      toast.success(`${repo!.name} is now the default repo`);
      refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteRepo(repo!.key),
    onSuccess: () => {
      refresh();
      navigate("/repos");
    },
    onError,
  });

  if (query.isLoading) return <PageSkeleton />;
  if (!repo) {
    return (
      <EmptyState icon={GitBranch} title="Repo not found">
        <Button variant="outline" asChild>
          <Link to="/repos">Back to repos</Link>
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <Breadcrumb className="text-xs">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/repos">Repos</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{repo.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="-mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex flex-wrap items-center gap-2 text-2xl leading-tight font-semibold tracking-[-0.022em]">
            <GitBranch className="size-5 text-muted-foreground" />
            {repo.name}
            <span className="font-mono text-sm text-muted-foreground">{repo.key}</span>
            {repo.isDefault && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400">
                <Star /> Default
              </Badge>
            )}
            {!repo.active && (
              <Badge variant="outline" className="text-muted-foreground">
                Inactive
              </Badge>
            )}
          </h1>
          <a
            href={repo.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 font-mono text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            {shortRepoUrl(repo.url)}
            <ArrowUpRight className="size-3" />
          </a>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/issues?repo=${repo.key}`}>
              {repo.issueCount} issue{repo.issueCount === 1 ? "" : "s"}
            </Link>
          </Button>
          {admin && !repo.isDefault && (
            <Button
              variant="outline"
              size="sm"
              disabled={!repo.active || setDefault.isPending}
              onClick={() => setDefault.mutate()}
            >
              <Star /> Make default
            </Button>
          )}
          {admin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={repo.isDefault || repo.issueCount > 0}
                  title={
                    repo.isDefault
                      ? "The default repo cannot be deleted"
                      : repo.issueCount > 0
                        ? "Repos with issues cannot be deleted"
                        : undefined
                  }
                >
                  <Trash2 /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {repo.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Nothing refers to this repo, so removing it only takes it out of the pickers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()}>Delete repo</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Checkout</CardTitle>
            <CardDescription>
              Stations clone this URL and start every branch from the default branch. Follow-up runs reuse the
              session they started with.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {admin ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="repo-name">Name</Label>
                  <Input
                    id="repo-name"
                    key={repo.name}
                    defaultValue={repo.name}
                    onBlur={(event) => {
                      const name = event.target.value.trim();
                      if (name && name !== repo.name) update.mutate({ name });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repo-url">URL</Label>
                  <Input
                    id="repo-url"
                    key={repo.url}
                    type="url"
                    className="font-mono"
                    defaultValue={repo.url}
                    onBlur={(event) => {
                      const url = event.target.value.trim();
                      if (url && url !== repo.url) update.mutate({ url });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repo-branch">Default branch</Label>
                  <Input
                    id="repo-branch"
                    key={repo.defaultBranch}
                    className="w-48 font-mono"
                    defaultValue={repo.defaultBranch}
                    onBlur={(event) => {
                      const defaultBranch = event.target.value.trim();
                      if (defaultBranch && defaultBranch !== repo.defaultBranch)
                        update.mutate({ defaultBranch });
                    }}
                  />
                </div>
              </>
            ) : (
              <dl className="grid gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">URL</dt>
                  <dd className="font-mono">{repo.url}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Default branch</dt>
                  <dd className="font-mono">{repo.defaultBranch}</dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Availability</CardTitle>
            <CardDescription>
              An active repo can be picked for new issues and specialist runs. The default repo is where
              issues land when nobody names one, and it stays active.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">
                  {repo.active
                    ? "Reporters and specialists can pick this repo."
                    : "Hidden from pickers. Existing issues keep it."}
                </p>
              </div>
              <Switch
                checked={repo.active}
                disabled={!admin || repo.isDefault || setActive.isPending}
                onCheckedChange={(active) => setActive.mutate(active)}
                aria-label={`${repo.name} active`}
              />
            </div>
            {repo.isDefault && (
              <p className="text-xs text-muted-foreground">
                This is the default repo. Make another repo the default before deactivating it.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
